import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, AuditSessionSummary, AuditStats, MessageFrequency } from '../services/adminApi'
import { useSort } from '../hooks/useSort'
import styles from './AuditPage.module.css'

export default function AuditPage() {
  const { apiKey } = useAdmin()

  const [sessions, setSessions] = useState<AuditSessionSummary[]>([])
  const [stats, setStats] = useState<AuditStats | null>(null)
  const sessionSort = useSort<AuditSessionSummary>(sessions, 'createdAt', 'desc')
  const userMsgSort = useSort<MessageFrequency>(stats?.topUserMessages ?? [], 'count', 'desc')
  const assistantMsgSort = useSort<MessageFrequency>(stats?.topAssistantMessages ?? [], 'count', 'desc')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const api = createAdminApi()
    setLoading(true)
    setError('')
    Promise.all([api.getAuditSessions(), api.getAuditStats()])
      .then(([sessionData, statsData]) => {
        setSessions(sessionData)
        setStats(statsData)
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey])

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString()
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Audit Log</h1>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.content}>
        {loading ? (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '2rem' }}>Loading…</p>
        ) : (
          <>
            {stats && (
              <>
                <div className={styles.statsBar}>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Sessions</span>
                    <span className={styles.statValue}>{stats.sessionCount.toLocaleString()}</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>LLM Requests</span>
                    <span className={styles.statValue}>{stats.requestCount.toLocaleString()}</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Messages</span>
                    <span className={styles.statValue}>{stats.messageCount.toLocaleString()}</span>
                  </div>
                </div>

                <div className={styles.topMessagesRow}>
                  {stats.topUserMessages.length > 0 && (
                    <div className={`${styles.section} ${styles.topMessagesSection}`}>
                      <div className={styles.sectionTitle}>Top User Messages</div>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th className={styles.sortable} onClick={() => userMsgSort.handleSort('content')}>
                              Content{userMsgSort.sortKey === 'content' ? (userMsgSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                            <th className={styles.sortable} onClick={() => userMsgSort.handleSort('count')}>
                              Count{userMsgSort.sortKey === 'count' ? (userMsgSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {userMsgSort.sorted.map((m, i) => (
                            <tr key={i}>
                              <td className={styles.contentCell} title={m.content}>{m.content}</td>
                              <td className={styles.countCell}>{m.count.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {stats.topAssistantMessages.length > 0 && (
                    <div className={`${styles.section} ${styles.topMessagesSection}`}>
                      <div className={styles.sectionTitle}>Top Assistant Messages</div>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th className={styles.sortable} onClick={() => assistantMsgSort.handleSort('content')}>
                              Content{assistantMsgSort.sortKey === 'content' ? (assistantMsgSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                            <th className={styles.sortable} onClick={() => assistantMsgSort.handleSort('count')}>
                              Count{assistantMsgSort.sortKey === 'count' ? (assistantMsgSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {assistantMsgSort.sorted.map((m, i) => (
                            <tr key={i}>
                              <td className={styles.contentCell} title={m.content}>{m.content}</td>
                              <td className={styles.countCell}>{m.count.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Sessions ({sessions.length})</div>
              {sessions.length === 0 ? (
                <p className={styles.empty}>No sessions logged yet.</p>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.sortable} onClick={() => sessionSort.handleSort('id')}>
                        Session ID{sessionSort.sortKey === 'id' ? (sessionSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                      <th className={styles.sortable} onClick={() => sessionSort.handleSort('createdAt')}>
                        Created{sessionSort.sortKey === 'createdAt' ? (sessionSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                      <th className={styles.sortable} onClick={() => sessionSort.handleSort('requestCount')}>
                        Requests{sessionSort.sortKey === 'requestCount' ? (sessionSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                      <th className={styles.sortable} onClick={() => sessionSort.handleSort('messageCount')}>
                        Messages{sessionSort.sortKey === 'messageCount' ? (sessionSort.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionSort.sorted.map(s => (
                      <tr
                        key={s.id}
                        className={styles.row}
                        onClick={() => { window.location.hash = `#audit/${s.id}` }}
                      >
                        <td className={styles.idCell}>
                          <a
                            href={`#audit/${s.id}`}
                            onClick={e => e.stopPropagation()}
                          >
                            {s.id.length > 12 ? `${s.id.slice(0, 12)}…` : s.id}
                          </a>
                        </td>
                        <td>{formatDate(s.createdAt)}</td>
                        <td>{s.requestCount}</td>
                        <td>{s.messageCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
